import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../hooks/useOrganization';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { ROUTES } from '../../config/constants';

export function AuthCallback() {
  const navigate = useNavigate();
  const { refetchProfile } = useAuth();
  const { hasOrganization, loading: orgLoading } = useOrganization();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Get the session to see if user is authenticated
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          throw new Error(`Authentication error: ${sessionError.message}`);
        }

        if (!session?.user) {
          throw new Error('No authenticated user found');
        }

        // Refresh the profile data
        await refetchProfile();

        setStatus('success');
        setMessage('Authentication successful! Redirecting...');

        // Wait for organization check to complete before navigating
        // This ensures the organization status is properly loaded
        const checkAndNavigate = () => {
          if (!orgLoading) {
            // Organization check is complete, navigate appropriately
            navigate(hasOrganization ? ROUTES.DASHBOARD : ROUTES.ONBOARDING);
          } else {
            // Still loading, check again in a moment
            setTimeout(checkAndNavigate, 100);
          }
        };

        // Start checking after a brief delay to show success message
        setTimeout(checkAndNavigate, 1000);

      } catch (error) {
        console.error('Auth callback error:', error);
        setStatus('error');
        
        // Provide more specific error messages
        if (error instanceof Error) {
          const errorMsg = error.message.toLowerCase();
          if (errorMsg.includes('network') || errorMsg.includes('fetch')) {
            setMessage('Network error. Please check your connection and try again.');
          } else if (errorMsg.includes('session') || errorMsg.includes('token')) {
            setMessage('Session expired. Please sign in again.');
          } else {
            setMessage(error.message);
          }
        } else {
          setMessage('An unexpected error occurred during authentication');
        }
      }
    };

    handleAuthCallback();
  }, [navigate, refetchProfile, hasOrganization, orgLoading]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <Card className="w-full max-w-lg mx-auto shadow-xl border-0">
        <CardHeader className="text-center pb-6">
          <div className="mx-auto h-16 w-16 bg-gradient-to-br from-slate-900 to-slate-700 rounded-xl flex items-center justify-center mb-4 shadow-lg">
            <span className="text-white font-bold text-xl">RM</span>
          </div>
          <CardTitle className="text-2xl font-bold text-slate-900">
            Authenticating
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {status === 'loading' && (
            <div className="text-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-600" />
              <p className="text-sm text-slate-600">Confirming your authentication...</p>
              <p className="text-sm text-slate-500">
                This may take a few moments. Please don't close this window.
              </p>
            </div>
          )}

          {status === 'success' && (
            <Alert className="bg-green-50 border-green-200 border-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <AlertTitle className="text-green-800 font-semibold">
                Authentication Successful!
              </AlertTitle>
              <AlertDescription className="text-green-700 mt-2 leading-relaxed">
                {message}
              </AlertDescription>
            </Alert>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <Alert variant="destructive" className="border-2">
                <XCircle className="h-5 w-5" />
                <AlertTitle>Authentication Error</AlertTitle>
                <AlertDescription className="mt-2">
                  {message}
                </AlertDescription>
              </Alert>
              <div className="flex gap-3">
                <Button 
                  onClick={() => navigate(ROUTES.LOGIN)}
                  className="flex-1 bg-slate-900 hover:bg-slate-800"
                >
                  Back to Login
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    window.open('mailto:support@topmatchtalent.com?subject=Authentication Assistance', '_blank');
                  }}
                  className="flex-1"
                >
                  Contact Support
                </Button>
              </div>
            </div>
          )}

          {status === 'success' && (
            <div className="flex justify-center pt-2">
              <Button 
                onClick={() => navigate(hasOrganization ? ROUTES.DASHBOARD : ROUTES.ONBOARDING)}
                className="bg-slate-900 hover:bg-slate-800 px-8 py-2 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
              >
                Continue →
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 